/**
 * Investigation Module API Service
 * Provides typed API client for all investigation endpoints.
 */

import { get, post, put } from '../utils/httpClient';

// ---- Types ----

export interface SearchRequest {
  phone_number?: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  document_number?: string;
  nationality?: string;
  date_of_birth?: string;
  hotel_id?: string;
  date_from?: string;
  date_to?: string;
  search_mode: 'exact' | 'fuzzy' | 'similar';
  fuzzy_threshold?: number;
  page: number;
  page_size: number;
}

export interface PersonSummary {
  guest_id: string;
  full_name: string;
  first_name?: string;
  last_name?: string;
  date_of_birth: string;
  nationality: string;
  document_type: string;
  document_number: string;
  phone_number: string;
  total_stays: number;
  last_stay_date?: string;
  active_alerts: number;
  match_score?: number;
}

export interface SearchResult {
  total_count: number;
  page: number;
  page_size: number;
  results: PersonSummary[];
}

export interface StayRecord {
  police_file_number: string;
  hotel_id: string;
  hotel_name: string;
  hotel_province: string;
  hotel_city: string;
  room_number: string;
  check_in_date: string;
  check_out_date?: string;
  stay_purpose: string;
  status: string;
  nights_count: number;
  has_alerts: boolean;
  companion_count: number;
}

export interface HotelVisitSummary {
  hotel_id: string;
  hotel_name: string;
  province: string;
  city: string;
  total_visits: number;
  first_visit: string;
  last_visit: string;
  total_nights: number;
}

export interface CompanionLink {
  person_id: string;
  full_name: string;
  relationship: string;
  shared_stays: number;
  first_seen: string;
  last_seen: string;
}

export interface AlertRecord {
  id: string;
  type: string;
  severity: string;
  status: string;
  message: string;
  created_at: string;
}

export interface IdentityMatchRecord {
  id: string;
  person_id_a: string;
  person_id_b: string;
  match_type: string;
  confidence: number;
  evidence?: Record<string, unknown>;
  confirmed_by?: string;
  confirmed_at?: string;
}

export interface GuestDetail {
  id: string;
  full_name: string;
  first_name?: string;
  last_name?: string;
  date_of_birth: string;
  nationality: string;
  document_type: string;
  document_number: string;
  phone_number: string;
}

export interface PersonStats {
  total_stays: number;
  total_hotels: number;
  total_nights: number;
  total_companions: number;
  active_alerts: number;
  identity_matches: number;
}

export interface ConsolidatedPerson {
  guest: GuestDetail;
  stay_history: StayRecord[];
  hotels_visited: HotelVisitSummary[];
  companions: CompanionLink[];
  alerts: AlertRecord[];
  identity_matches: IdentityMatchRecord[];
  stats: PersonStats;
}

export interface HotelGeoData {
  hotel_id: string;
  name: string;
  latitude: number | null;
  longitude: number | null;
  full_address: string;
  province: string;
  city: string;
  commune: string;
  quartier: string;
  altitude?: number;
  access_notes?: string;
  nearby_landmarks?: string;
  map_tile_url?: string;
}

export interface GeoSearchRequest {
  latitude: number;
  longitude: number;
  radius_km: number;
  limit: number;
}

export interface GeoSearchResult {
  hotels: HotelGeoData[];
  center: { latitude: number; longitude: number };
  radius_km: number;
}

export interface MatchAlert {
  id: string;
  alert_type: string;
  severity: string;
  status: string;
  primary_guest_id: string;
  secondary_guest_id: string;
  primary_guest_name?: string;
  secondary_guest_name?: string;
  match_field: string;
  match_value: string;
  match_score: number;
  resolved_by?: string;
  resolved_at?: string;
  resolution?: string;
  resolution_note?: string;
  detected_at: string;
  batch_id?: string;
}

export interface MatchAlertFilters {
  type?: string;
  severity?: string;
  status?: string;
  page?: number;
  page_size?: number;
}

export interface ResolveAlertRequest {
  resolution: 'CONFIRMED_MATCH' | 'FALSE_POSITIVE' | 'MERGED';
  resolution_note?: string;
}

// ---- API Functions ----

export async function searchInvestigation(req: SearchRequest): Promise<SearchResult> {
  const response = await post('/api/v1/investigation/search', req);
  if (!response.ok) {
    throw new Error('Erreur lors de la recherche investigation');
  }
  return response.json();
}

export async function getConsolidatedPerson(id: string): Promise<ConsolidatedPerson> {
  const response = await get(`/api/v1/investigation/persons/${id}/consolidated`);
  if (!response.ok) {
    if (response.status === 404) throw new Error('Personne non trouvée');
    throw new Error('Erreur lors de la récupération du dossier consolidé');
  }
  return response.json();
}

export async function getHotelGeo(id: string): Promise<HotelGeoData> {
  const response = await get(`/api/v1/investigation/hotels/${id}/geo`);
  if (!response.ok) {
    throw new Error('Erreur lors de la récupération des données géographiques');
  }
  return response.json();
}

export async function searchNearbyHotels(req: GeoSearchRequest): Promise<GeoSearchResult> {
  const response = await post('/api/v1/investigation/hotels/nearby', req);
  if (!response.ok) {
    throw new Error('Erreur lors de la recherche de proximité');
  }
  return response.json();
}

export async function getMatchAlerts(filters?: MatchAlertFilters): Promise<{ alerts: MatchAlert[]; total: number }> {
  const params = new URLSearchParams();
  if (filters?.type) params.set('type', filters.type);
  if (filters?.severity) params.set('severity', filters.severity);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.page_size) params.set('page_size', String(filters.page_size));
  const qs = params.toString();
  const endpoint = `/api/v1/investigation/match/alerts${qs ? `?${qs}` : ''}`;
  const response = await get(endpoint);
  if (!response.ok) {
    throw new Error('Erreur lors de la récupération des alertes');
  }
  return response.json();
}

export async function resolveMatchAlert(id: string, resolution: ResolveAlertRequest): Promise<void> {
  const response = await put(`/api/v1/investigation/match/alerts/${id}/resolve`, resolution);
  if (!response.ok) {
    throw new Error('Erreur lors de la résolution de l\'alerte');
  }
}

export async function triggerMatchScan(): Promise<{ batch_id: string; total_created: number }> {
  const response = await post('/api/v1/investigation/match/scan');
  if (!response.ok) {
    throw new Error('Erreur lors du déclenchement du scan');
  }
  return response.json();
}
